import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from "@/theme/ThemeComponent";
import SimpleParallax from 'simple-parallax-js';

@Component
export class ProjectInsight extends ThemeComponent {
    @Ref({ required: false }) scrollImage!: HTMLElement[];
    @Ref({ required: false }) details!: HTMLElement;
    @Ref({ required: false }) smallImage!: HTMLElement;
    @Prop({ default: 98 }) break!: number;
    @Prop({ default: 1.1 }) scale!: number;

    private animationElements: HTMLElement[] = [];
    private isAnimating = false;
    private lastScrollY = 0;
    private previousScrollY = 0; // Add this to track previous position
    private scrollDirection = 0; // 1 for down, -1 for up, 0 for no change
    private cumulativeContentOffset = 0; // Track cumulative movement for content
    private cumulativeImageOffset = 0; // Track cumulative movement for image

    init() {
        this.handleImgAnimation();
        // Only initialize animations if screen width is 1024px or larger
        if (this.isDesktopOrTablet()) {
            this.handleScrollAnimations();
        }
    }

    private isDesktopOrTablet(): boolean {
        return window.innerWidth >= 1024;
    }

    protected handleImgAnimation() {
        if (!this.scrollImage) { return; }

        for (const image of this.scrollImage) {
            new SimpleParallax(image, {
                delay: 0,
                scale: this.scale,
                transition: 'linear',
                orientation: 'up',
                maxTransition: this.break
            });
        }
    }

    protected handleScrollAnimations() {
        // Collect all elements to animate
        if (this.details) {
            this.animationElements.push(this.details);
        }
        if (this.smallImage) {
            this.animationElements.push(this.smallImage);
        }

        if (this.animationElements.length === 0) {
            return;
        }

        // Set initial states
        this.setInitialStates();

        // Add scroll listener
        window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

        // Initial call to set positions
        this.handleScroll();
    }

    private setInitialStates() {
        // Set initial transform states for smooth animation
        if (this.details) {
            // Get the current transform to preserve existing positioning
            const currentTransform = this.details.style.transform || '';
            // Start at 0% (no additional offset)
            this.details.style.transform = currentTransform + ' translateY(0%)';
            this.details.style.transition =
                'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.6s ease-out';
            // Set initial background opacity to 1
            this.details.style.opacity = '1';
        }

        if (this.smallImage) {
            this.smallImage.style.transform = 'translateY(0%)';
            this.smallImage.style.transition = 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        }
    }

    private handleScroll() {
        if (this.isAnimating) {return;}

        requestAnimationFrame(() => {
            this.updateAnimations();
        });
    }

    private updateAnimations() {
        const currentScrollY = window.scrollY;
        const scrollDelta = currentScrollY - this.lastScrollY;

        // Determine scroll direction
        if (Math.abs(scrollDelta) > 1) { // Threshold to avoid micro-movements
            this.scrollDirection = scrollDelta > 0 ? 1 : -1;
        } else {
            this.scrollDirection = 0;
        }

        this.previousScrollY = this.lastScrollY; // Store previous position
        this.lastScrollY = currentScrollY;

        const windowHeight = window.innerHeight;
        const sectionRect = this.getBoundingClientRect();

        // Check if section is in viewport
        const isInView = sectionRect.top < windowHeight && sectionRect.bottom > 0;

        if (isInView) {
            this.applyScrollAnimation();
        } else {
            // Reset to initial position when out of view
            this.resetToInitialPosition();
        }
    }

    private applyScrollAnimation() {
        const windowHeight = window.innerHeight;
        const sectionRect = this.getBoundingClientRect();

        // Calculate section's position within viewport (0 = top, 1 = bottom)
        const sectionTop = sectionRect.top;
        const sectionHeight = sectionRect.height;
        const viewportProgress = Math.max(0, Math.min(1, (windowHeight - sectionTop) / (windowHeight + sectionHeight)));

        // Calculate movement based on section position
        const maxContentOffset = windowHeight * 0.3; // Content moves 30% of viewport height
        const maxImageOffset = windowHeight * 0.5; // Image moves 50% of viewport height

        // Map section progress to movement range - INVERTED for correct direction
        const contentOffsetY = maxContentOffset * (0.5 - viewportProgress) * 2; // Inverted: center at 0.5
        const imageOffsetY = maxImageOffset * (0.5 - viewportProgress) * 2; // Inverted: center at 0.5

        // Apply animation to details (content) - moves slower and less
        if (this.details) {
            const originalTransform = this.getOriginalTransform(this.details);
            this.details.style.transform = originalTransform + ` translateY(${contentOffsetY}px)`;
        }

        // Apply animation to small image - moves faster and more
        if (this.smallImage) {
            this.smallImage.style.transform = `translateY(${imageOffsetY}px)`;
        }
    }

    private resetToInitialPosition() {
        // Reset cumulative offsets when out of view
        this.cumulativeContentOffset = 0;
        this.cumulativeImageOffset = 0;

        if (this.details) {
            const originalTransform = this.getOriginalTransform(this.details);
            this.details.style.transform = originalTransform + ' translateY(0%)';
        }
        if (this.smallImage) {
            this.smallImage.style.transform = 'translateY(0%)';
        }
    }

    private getOriginalTransform(element: HTMLElement): string {
        // Extract the original transform based on CSS classes
        if (element.classList.contains('project-insights-details--centre')) {
            return 'translate(-50%, -50%)';
        } else if (element.classList.contains('project-insights-details--right')) {
            if (element.classList.contains('project-insights-details--right-reverse')) {
                return 'translate(-25%, -50%)';
            } else {
                return 'translate(25%, -50%)';
            }
        }
        return '';
    }

    destroy() {
        window.removeEventListener('scroll', this.handleScroll.bind(this));
    }
}
